import { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { format, parseISO, isValid, startOfWeek, addWeeks, addDays } from 'date-fns'
import { Info } from 'lucide-react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog'
import { cn } from '../../lib/utils'
import { weekKindBadgeClass } from '../training-plans/plan-helpers'
import type { TrainingPlan } from '../../types/training-plan'
import type { WorkoutResult } from '../../types/workout'

interface TrainingPlanWeekBannerProps {
  plan: TrainingPlan
  weekNumber: number
  /** Logged results for the currently-viewed week, used for the progress bar. */
  weekResults: WorkoutResult[]
}

/**
 * Shown on the diary below the week selector when an active plan covers the
 * visible week: the plan range, the week's type (base/rest) with a description
 * dialog, and a progress bar of completed vs planned sessions for the week.
 */
export function TrainingPlanWeekBanner({ plan, weekNumber, weekResults }: TrainingPlanWeekBannerProps) {
  const { t } = useTranslation()
  const [descriptionOpen, setDescriptionOpen] = useState(false)

  const week = plan.weeks.find((w) => w.week_number === weekNumber)
  const kind = week?.kind ?? 'base'
  const description = week?.description ?? null

  // Total = planned sessions for the week (sum of cell frequencies); completed =
  // sessions with a logged result, capped per exercise at its target.
  const { total, completed } = useMemo(() => {
    const cells = plan.cells.filter((c) => c.week_number === weekNumber)
    const resultsByExercise = new Map<string, number>()
    for (const result of weekResults) {
      const key = result.exercise.id
      resultsByExercise.set(key, (resultsByExercise.get(key) ?? 0) + 1)
    }
    return cells.reduce(
      (acc, cell) => {
        const done = resultsByExercise.get(String(cell.exercise_id)) ?? 0
        return { total: acc.total + cell.frequency, completed: acc.completed + Math.min(done, cell.frequency) }
      },
      { total: 0, completed: 0 }
    )
  }, [plan.cells, weekNumber, weekResults])

  const percent = total > 0 ? Math.round((completed / total) * 100) : 0
  const planLabel = plan.goal?.trim() || t(`trainingPlans.discipline.${plan.discipline ?? 'bouldering'}`)

  const planStart = plan.start_date ? parseISO(plan.start_date) : null
  const hasRange = planStart != null && isValid(planStart) && !!plan.length_weeks
  const startLabel = hasRange ? format(planStart as Date, 'dd/MM') : null
  const endLabel = hasRange
    ? format(addDays(addWeeks(startOfWeek(planStart as Date, { weekStartsOn: 1 }), (plan.length_weeks as number) - 1), 6), 'dd/MM')
    : null

  return (
    <div className="rounded-xl border bg-card p-3 flex flex-col gap-2.5">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <Link
            to={`/training-plans/${plan.id}`}
            className="block font-medium truncate hover:text-primary hover:underline transition-colors"
          >
            {planLabel}
          </Link>
          {startLabel && endLabel && (
            <p className="text-xs text-muted-foreground">{startLabel} – {endLabel}</p>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-xs text-muted-foreground hidden sm:inline">
            {t('trainingDiary.planBanner.week', { number: weekNumber })}
          </span>
          <span
            className={cn(
              'rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
              weekKindBadgeClass(kind)
            )}
          >
            {t(`trainingPlans.weekKind.${kind}`)}
          </span>
          <button
            type="button"
            onClick={() => setDescriptionOpen(true)}
            aria-label={t('trainingDiary.planBanner.descriptionTitle', { number: weekNumber })}
            className="rounded-full p-1 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          >
            <Info className="size-4" />
          </button>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <div className="h-2 flex-1 rounded-full bg-muted overflow-hidden">
          <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${percent}%` }} />
        </div>
        <span className="text-xs text-muted-foreground tabular-nums">{completed}/{total}</span>
      </div>

      <Dialog open={descriptionOpen} onOpenChange={setDescriptionOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>{t('trainingDiary.planBanner.descriptionTitle', { number: weekNumber })}</DialogTitle>
          </DialogHeader>
          <p className="text-sm whitespace-pre-wrap break-words">
            {description && description.trim() !== ''
              ? description
              : t('trainingDiary.planBanner.noDescription')}
          </p>
        </DialogContent>
      </Dialog>
    </div>
  )
}
